import { useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthLayout } from '@/components/auth/AuthLayout';
import { FormField } from '@/components/auth/FormField';
import { SubmitButton } from '@/components/auth/SubmitButton';
import { AuthError } from '@/components/auth/AuthError';
import { AuthSuccess } from '@/components/auth/AuthSuccess';
import { supabase } from '@/lib/supabase';
import { describeAuthError } from '@/lib/authErrors';
import { validateEmail } from '@/lib/validation';

interface LocationState {
  successMessage?: string;
  from?: string;
}

export default function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state ?? null) as LocationState | null;
  const successFromState = state?.successMessage ?? null;
  const redirectTo = state?.from && state.from !== '/login' ? state.from : '/game';

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState<{ email?: string; password?: string }>({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setSubmitError(null);

    const errors: { email?: string; password?: string } = {};
    const emailError = validateEmail(email);
    if (emailError) errors.email = emailError;
    if (!password) errors.password = 'Enter your password.';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }
    setFieldErrors({});
    setLoading(true);

    try {
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        setSubmitError(describeAuthError(error));
        return;
      }

      navigate(redirectTo, { replace: true });
    } catch (err) {
      setSubmitError(describeAuthError(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout
      title="Set Sail"
      subtitle="Sign in to your account."
      footer={
        <>
          New to these waters?{' '}
          <Link to="/signup" className="text-ink underline hover:text-candle-dark">
            Enlist
          </Link>
        </>
      }
    >
      {successFromState && <AuthSuccess>{successFromState}</AuthSuccess>}
      <AuthError message={submitError} />

      <form onSubmit={handleSubmit} noValidate>
        <FormField
          name="email"
          label="Email"
          type="email"
          autoComplete="email"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          error={fieldErrors.email}
          required
        />

        <FormField
          name="password"
          label="Password"
          type="password"
          autoComplete="current-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={fieldErrors.password}
          required
        />

        <div className="-mt-2 mb-4 text-right">
          <Link
            to="/forgot-password"
            className="text-xs text-ink-mid underline hover:text-candle-dark"
          >
            Forgot password?
          </Link>
        </div>

        <SubmitButton loading={loading}>{loading ? 'Casting off…' : 'Sign In'}</SubmitButton>
      </form>
    </AuthLayout>
  );
}
